import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { getApiEndpoint } from "@/lib/api";
import { setAuth } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";

interface CompanyInfo {
  id: number;
  name: string;
  domain: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    const saved = localStorage.getItem("tec-it-remember-email");
    if (saved) {
      setEmail(saved);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const extractDomain = (email: string): string | null => {
      if (!email || !email.includes("@")) return null;
      const parts = email.split("@");
      return parts.length === 2 ? parts[1].toLowerCase().trim() : null;
    };
    const domain = extractDomain(email);
    const timeoutId = setTimeout(() => {
      if (domain && domain.length > 3) {
        setIsLoadingCompany(true);
        fetch(getApiEndpoint(`/api/companies/domain/${domain}`))
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => setCompanyInfo(data?.company ?? null))
          .catch(() => setCompanyInfo(null))
          .finally(() => setIsLoadingCompany(false));
      } else {
        setCompanyInfo(null);
        setIsLoadingCompany(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    if (rememberMe) {
      localStorage.setItem("tec-it-remember-email", email);
    } else {
      localStorage.removeItem("tec-it-remember-email");
    }
    try {
      const response = await fetch(getApiEndpoint("/api/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok) {
        if (data?.force_password_reset && data?.reset_token && data?.reset_email) {
          toast({ title: t.auth.forcePasswordReset, description: t.auth.setNewPassword });
          navigate(`/reset-password?token=${encodeURIComponent(data.reset_token)}&email=${encodeURIComponent(data.reset_email)}`);
          return;
        }
        setAuth(data.token, data.user, rememberMe);
        window.dispatchEvent(new Event("userChanged"));
        toast({ title: t.auth.loginSuccess, description: t.auth.redirecting });
        if (data.user.role === "internal" || data.user.role === "analyst" || data.user.role === "admin") {
          navigate("/atendimento");
        } else {
          navigate("/chamados");
        }
      } else {
        toast({ title: t.auth.loginError, description: data.error || t.auth.invalidCredentials, variant: "destructive" });
      }
    } catch {
      toast({ title: t.auth.loginError, description: t.auth.connectionError, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const mascotSrc = "/login-assets/tec-it-mascot.jpeg";
  const videoSrc = "/login-assets/login-background.mp4";

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0f172a]">
      {/* Background video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm dark:bg-[#0f172a]/80" />
      {/* Gradient effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      {/* Watermark mascot - bottom right */}
      <div className="absolute bottom-6 right-6 opacity-20 hover:opacity-40 transition-opacity duration-500 z-0">
        <div className="w-20 h-20 rounded-full overflow-hidden">
          <img src={mascotSrc} alt="" className="w-full h-full object-cover object-top scale-110 grayscale" />
        </div>
      </div>

      {/* Login Card */}
      <div className="relative w-full max-w-md animate-fade-in z-10">
        <div className="glass-card rounded-2xl p-8 glow-primary dark:bg-gray-900/90 dark:border-gray-700/50">
          {/* Mascot Logo */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-violet-500/30 shadow-2xl dark:border-violet-400/30">
                <img
                  src={companyInfo?.logo_url ? getApiEndpoint(companyInfo.logo_url) : mascotSrc}
                  alt="TEC-IT"
                  className="w-full h-full object-cover object-top scale-110"
                  onError={(e) => { (e.target as HTMLImageElement).src = mascotSrc; }}
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-gray-900 dark:border-gray-900 shadow-lg">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2 dark:text-gray-400">
              {t.auth.centralDeServicos}
            </p>
            <h1 className="text-2xl font-semibold text-foreground dark:text-white">
              {companyInfo ? companyInfo.name.split(" ")[0] ?? companyInfo.name : "TEC-IT"}{" "}
              <span className="text-violet-500 dark:text-violet-400">
                {companyInfo && companyInfo.name.includes(" ") ? companyInfo.name.split(" ").slice(1).join(" ") : "ITSM"}
              </span>
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-muted-foreground text-sm dark:text-gray-400">
                {t.auth.email}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t.auth.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 bg-input dark:bg-gray-800 border-border/50 dark:border-gray-600 focus:border-violet-500 focus:ring-violet-500/20 transition-all duration-300 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-muted-foreground text-sm dark:text-gray-400">
                {t.auth.password}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 pr-11 h-12 bg-input dark:bg-gray-800 border-border/50 dark:border-gray-600 focus:border-violet-500 focus:ring-violet-500/20 transition-all duration-300 dark:text-white"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(!!v)}
                  className="border-border/50 dark:border-gray-600 data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500"
                />
                <Label htmlFor="remember" className="text-sm text-muted-foreground dark:text-gray-400 cursor-pointer">
                  {t.auth.rememberMe}
                </Label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm text-violet-500 hover:text-violet-400 dark:text-violet-400 dark:hover:text-violet-300 transition-colors"
              >
                {t.auth.forgotPasswordAlt}
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-violet-600 hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-700 text-white font-medium transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25"
              disabled={isLoading}
            >
              {isLoading ? t.auth.signingIn : t.auth.accessSystem}
            </Button>

            <Link to="/cadastro" className="block">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-emerald-500 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400 hover:bg-emerald-500 hover:text-white dark:hover:bg-emerald-600 dark:hover:text-white font-medium transition-all duration-300"
              >
                {t.auth.registerCta}
              </Button>
            </Link>

            {isLoadingCompany && (
              <p className="text-center text-xs text-muted-foreground dark:text-gray-500">{t.auth.detectingCompany}</p>
            )}
          </form>

          {/* Footer */}
          <p className="text-center text-muted-foreground dark:text-gray-500 text-xs mt-8">
            {t.auth.tagline}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
